"use client";

import { Check, Languages } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { LanguageFlag } from "@/components/i18n/language-flag";
import {
	LIVE_SITE_LOCALE_DEFINITIONS,
	DEFAULT_SITE_LOCALE,
	getSiteLocaleDefinition,
	isLiveSiteLocale,
	type LiveSiteLocale,
} from "@/i18n/locales";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/utils/ui";

const HEADER_PATHNAMES = [
	"/",
	"/about",
	"/privacy",
	"/terms",
	"/tools/video-converter",
	"/tools/video-to-gif",
	"/tools/resize-video",
	"/tools/screen-recorder",
	"/tools/crop-video",
	"/tools/rotate-video",
	"/tools/video-compressor",
	"/projects",
] as const;

type HeaderPathname = (typeof HEADER_PATHNAMES)[number];
const HEADER_PATHNAME_SET: ReadonlySet<string> = new Set(HEADER_PATHNAMES);

function isHeaderPathname(pathname: string): pathname is HeaderPathname {
	return HEADER_PATHNAME_SET.has(pathname);
}

export function LanguageSelector({
	className,
	editorProjectId,
}: {
	className?: string;
	editorProjectId?: string;
}) {
	const t = useTranslations("Languages");
	const requestedLocale = useLocale();
	const locale: LiveSiteLocale = isLiveSiteLocale(requestedLocale)
		? requestedLocale
		: DEFAULT_SITE_LOCALE;
	const pathname = usePathname();
	const targetPathname = isHeaderPathname(pathname) ? pathname : "/";
	const targetHref = editorProjectId
		? {
				pathname: "/editor/[project_id]" as const,
				params: { project_id: editorProjectId },
			}
		: targetPathname;
	const currentLanguage = getSiteLocaleDefinition(locale);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					className={cn("size-11 rounded-full p-0", className)}
					aria-label={t("selectorLabel", {
						language: currentLanguage.label,
					})}
				>
					<Languages className="size-4.5" aria-hidden="true" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				className="scrollbar-thin min-w-56 overscroll-contain p-2"
				style={{
					maxHeight:
						"min(55vh, 30rem, var(--radix-dropdown-menu-content-available-height))",
					overflowY: "auto",
				}}
			>
				{LIVE_SITE_LOCALE_DEFINITIONS.map((language) => (
					<DropdownMenuItem key={language.locale} asChild className="p-0">
						<Link
							href={targetHref}
							locale={language.locale}
							hrefLang={language.hrefLang}
							aria-current={language.locale === locale ? "page" : undefined}
							className="flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2"
						>
							<LanguageFlag locale={language.locale} />
							<span className="min-w-0 flex-1 truncate font-medium">
								{language.label}
							</span>
							{language.locale === locale ? (
								<Check className="text-primary size-4" aria-hidden="true" />
							) : null}
						</Link>
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
